import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import {
  PricingInfo as ApiPricingInfo,
  getPricingInfo,
} from '@shared/pricingInfoApi';
import {
  COUPON_DISCOUNT,
  TABLE_PRICE,
  VIP_DISCOUNT,
} from '@shared/types/transactionLineItems';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { TransactionLineItem } from './apiHelpers';

export type PricingInfo = ApiPricingInfo & {
  subtotal: number;
  lineItems: TransactionLineItem[];
};

export type AppliedCheckoutCouponPricing = {
  discountAmount: number;
};

export type AppliedVipDiscountPricing = {
  discountAmount: number;
  originalPurchasePrice: number;
};

type PricingQueryData = ApiPricingInfo & {
  couponDiscountAmount: number | null;
  initialTablePrice: number;
  subtotal: number;
  vipDiscountAmount: number | null;
};

export const usePricingInfo = (
  zipCode: string,
  initialTablePrice?: number | null,
  appliedCoupon?: AppliedCheckoutCouponPricing | null,
  appliedVipDiscount?: AppliedVipDiscountPricing | null,
  restaurantId?: string,
  isInitialPurchase?: boolean,
) => {
  // VIP discount and coupon are mutually exclusive: a VIP tier wins. The UI
  // hides the coupon section when a tier is present, so this guards against a
  // stale coupon coexisting with a freshly-loaded tier.
  const vipDiscountAmount =
    appliedVipDiscount && appliedVipDiscount.discountAmount > 0
      ? appliedVipDiscount.discountAmount
      : null;
  const couponDiscountAmount =
    vipDiscountAmount == null &&
    appliedCoupon &&
    appliedCoupon.discountAmount > 0
      ? appliedCoupon.discountAmount
      : null;
  const originalTablePrice =
    appliedVipDiscount?.originalPurchasePrice ?? initialTablePrice;
  const discountedTablePrice =
    originalTablePrice != null && couponDiscountAmount != null
      ? Math.max(0, originalTablePrice - couponDiscountAmount)
      : null;
  const vipDiscountedTablePrice =
    originalTablePrice != null && vipDiscountAmount != null
      ? Math.max(0, originalTablePrice - vipDiscountAmount)
      : null;

  const debouncedInitialTablePrice = useDebouncedValue(initialTablePrice, 1000);
  const chargedPrice =
    vipDiscountedTablePrice ??
    discountedTablePrice ??
    debouncedInitialTablePrice;

  return useQuery({
    queryKey: [
      'pricingInfo',
      zipCode,
      restaurantId,
      isInitialPurchase,
      chargedPrice,
      couponDiscountAmount,
      vipDiscountAmount,
      originalTablePrice,
    ],
    queryFn: async (): Promise<PricingQueryData> => {
      const pricingInfo = await getPricingInfo({
        price: chargedPrice as number,
        zipCode,
        restaurantId,
        isInitialPurchase,
      });

      return {
        ...pricingInfo,
        couponDiscountAmount,
        initialTablePrice: originalTablePrice as number,
        subtotal: chargedPrice as number,
        vipDiscountAmount,
      };
    },
    enabled: chargedPrice != null,
    placeholderData: keepPreviousData,
    select: (pricingInfo) => {
      const lineItems: TransactionLineItem[] = [
        {
          name: TABLE_PRICE,
          quantity: 1,
          pricePerUnit: pricingInfo.initialTablePrice,
        },
        ...(pricingInfo.vipDiscountAmount != null
          ? [
              {
                name: VIP_DISCOUNT,
                quantity: 1,
                pricePerUnit: -pricingInfo.vipDiscountAmount,
              },
            ]
          : []),
        ...(pricingInfo.couponDiscountAmount != null
          ? [
              {
                name: COUPON_DISCOUNT,
                quantity: 1,
                pricePerUnit: -pricingInfo.couponDiscountAmount,
              },
            ]
          : []),
      ];

      return {
        fee: pricingInfo.fee,
        tax: pricingInfo.tax,
        total: pricingInfo.total,
        subtotal: pricingInfo.subtotal,
        lineItems,
      };
    },
  });
};
